import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Partner } from '../api.js';
import { TabStrip } from '../components/TabStrip.js';
import type { AdminHttpClient } from '../http.js';
import type { Tenant, ToastMessage } from '../types.js';
import { describeError } from '../errors.js';
import { PartnerAdminsTab } from './partner/PartnerAdminsTab.js';
import { PartnerAnnouncementsTab } from './partner/PartnerAnnouncementsTab.js';
import { PartnerCustomModelsTab } from './partner/PartnerCustomModelsTab.js';
import { PartnerOverviewTab } from './partner/PartnerOverviewTab.js';
import { PartnerTenantCreditsTab } from './partner/PartnerTenantCreditsTab.js';
import { PartnerTenantsTab } from './partner/PartnerTenantsTab.js';
import { PartnerToolScopeTab } from './partner/PartnerToolScopeTab.js';
import { PartnerSignupTab } from './partner/PartnerSignupTab.js';

const TABS = [
  { id: 'overview', label: 'partner.tabOverview' },
  { id: 'tenants', label: 'partner.tabTenants' },
  { id: 'admins', label: 'partner.tabAdmins' },
  { id: 'tenantCredits', label: 'partner.tabTenantCredits' },
  { id: 'customModels', label: 'partner.tabCustomModels' },
  { id: 'toolScope', label: 'partner.tabToolScope' },
  { id: 'selfSignup', label: 'partner.tabSelfSignup' },
  { id: 'announcements', label: 'partner.tabAnnouncements' },
] as const;
type TabId = (typeof TABS)[number]['id'];

export function customCatalogModelId(tenantId: string, modelKey: string): string {
  return `${tenantId}-custom-${modelKey}`;
}

export function PartnerScreen({
  http,
  onToast,
  onSetTenantContext,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  onSetTenantContext: (tenantId: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('overview');
  const [partner, setPartner] = useState<Partner>();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [self, ownedTenants] = await Promise.all([
        http.partnerSelf(),
        http.partnerListTenants(),
      ]);
      setPartner(self);
      setTenants(ownedTenants);
      setSelectedTenantId((current) =>
        ownedTenants.some((tenant) => tenant.id === current) ? current : (ownedTenants[0]?.id ?? '')
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <section className="content-stack">
      <section className="panel">
        <h1>{t('partner.selfServiceTitle')}</h1>
      </section>
      <TabStrip
        tabs={TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
        value={tab}
        onChange={setTab}
      />
      <div hidden={tab !== 'overview'}>
        <PartnerOverviewTab http={http} onToast={onToast} active={tab === 'overview'} />
      </div>
      <div hidden={tab !== 'tenants'}>
        <PartnerTenantsTab
          http={http}
          partner={partner}
          tenants={tenants}
          busy={busy}
          setBusy={setBusy}
          onReload={reload}
          onSetTenantContext={onSetTenantContext}
          onToast={onToast}
        />
      </div>
      <div hidden={tab !== 'admins'}>
        <PartnerAdminsTab http={http} onToast={onToast} active={tab === 'admins'} />
      </div>
      <div hidden={tab !== 'tenantCredits'}>
        <PartnerTenantCreditsTab
          http={http}
          tenants={tenants}
          tenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
          onToast={onToast}
          active={tab === 'tenantCredits'}
        />
      </div>
      <div hidden={tab !== 'customModels'}>
        <PartnerCustomModelsTab
          http={http}
          tenants={tenants}
          tenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
          onToast={onToast}
          active={tab === 'customModels'}
        />
      </div>
      <div hidden={tab !== 'toolScope'}>
        <PartnerToolScopeTab
          http={http}
          tenants={tenants}
          tenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
          onToast={onToast}
          active={tab === 'toolScope'}
        />
      </div>
      <div hidden={tab !== 'selfSignup'}>
        <PartnerSignupTab
          http={http}
          tenants={tenants}
          tenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
          onToast={onToast}
        />
      </div>
      <div hidden={tab !== 'announcements'}>
        <PartnerAnnouncementsTab http={http} onToast={onToast} active={tab === 'announcements'} />
      </div>
    </section>
  );
}
